<template>
  <section
    data-test="date-picker"
    class="date-picker"
    :class="{
      'date-picker-without-availability': !dateAvailability,
      desktop: $isDesktop,
      'dates-selected': dates.arrive && dates.depart,
      'date-picker-months-2': showMonths === 2,
    }"
  >
    <slot name="pre-header" />

    <dates-wizard-body-header-range
      v-if="showHeaderRange && datePickerConfig"
      :start="headerRangeDates.arrive"
      :end="headerRangeDates.depart"
    />

    <!-- Extra <div> Prevents https://gitlab.pitchup.com/pitchup/alfred/-/issues/2829 -->
    <div>
      <date-range-picker
        v-if="datePickerConfig"
        ref="date-picker"
        v-model="datePickerModel"
        :config="datePickerConfig"
        readonly="readonly"
        tabindex="-1"
        class="date-range-picker-input"
        @on-change="onDatePickerDatesChange"
        @on-month-change="onDatePickerMonthChange"
        @on-year-change="onDatePickerYearChange"
      />
    </div>

    <slot
      name="categories"
      :showing-arrival-days="showingArrivalDays"
      :selection-tag-class="selectionTagClass"
    />

    <dates-wizard-body-clear-button
      :class="{ 'disabled-button': !start }"
      @click="clearDates"
    />
    <nuxt-to-meta :nuxt2meta="head" />
  </section>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
/* TODO: Add proper typing to resolve type safety warnings */
import { defineAsyncComponent, nextTick } from 'vue'
import type {
  DayElement as FlatPickrDayElement,
  Instance as FlatPickrInstance,
} from 'flatpickr/dist/types/instance'
import type { DateRangePicker } from 'vue-flatpickr-component'
import { puNumber } from '~/lang/formatters/puNumber'
import { useCurrencyStore } from '~/stores/useCurrencyStore'
import { DateRange } from '~/utility/date/DateRange'
import { IsoDateFormatter } from '~/utility/date/IsoDateFormatter'
import { addDays } from '~/utility/date/relative'
import { SimpleMoney, priceToMoneyType } from '~/utility/money'
import { getMinFractionDigits } from '~/utility/formatCurrency'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { CampsiteCategory } from '~/utility/types/CampsiteCategory'
import { Nullable } from '~/utility/types/Nullable'
import { AvailabilityResults } from '../../../apps/search-wizard/availability/schemas'
import { OpeningDatesRange } from '../../../apps/search-wizard/types'
import { ArriveDepartIso } from '../../../apps/search/types'
import { getDatePickerConfig } from './getDatePickerConfig'
import { ChargetypeCalendarDay } from './types'

@Component({
  components: {
    'date-range-picker': defineAsyncComponent(
      () => import('vue-flatpickr-component'),
    ),
  },
  emits: [
    'monthChanged',
    'yearChanged',
    'datesChanged',
    'changeDates',
    'clearDates',
    'changeRange',
  ],
  setup() {
    const currencyStore = useCurrencyStore()
    return {
      currencyStore,
    }
  },
})
class DatesWizardBody extends Vue {
  currencyStore: ReturnType<typeof useCurrencyStore>
  @Prop({ required: true })
  dates: Nullable<ArriveDepartIso>

  @Prop({ required: false, default: true })
  showHeaderRange: boolean

  @Prop({ required: false })
  openingDates?: OpeningDatesRange[]

  @Prop()
  calendarStartDate: string

  @Prop()
  availability: AvailabilityResults

  get dateAvailability() {
    return this.$route.params.slug ? this.availability : []
  }

  @Prop()
  chargetypeCalendarData: { [key: string]: ChargetypeCalendarDay }

  @Prop()
  categoryDetails: CampsiteCategory[]

  @Prop()
  selectedDatesPrice: SimpleMoney

  @Prop({ default: 1 })
  showMonths: number

  @Prop({ default: false })
  showAsterix: boolean

  get head() {
    return {
      bodyAttrs: {
        class: this.$isDesktop ? '' : 'noscroll',
      },
    }
  }

  private currentMonth = 0
  private currentYear = 0
  datePickerConfig?: Awaited<ReturnType<typeof getDatePickerConfig>> | null =
    null

  datePickerModel: string | Date | null | Array<string | Date | null> = null

  private setPickerState(
    start: string | Date | null = null,
    end: string | Date | null = null,
  ) {
    this.datePickerModel = getDatePickerModel(start, end)
  }

  showingArrivalDays = true
  selectionTagClass = 'date-picker__selection-key-desc'

  async beforeMount() {
    this.datePickerConfig = await this.createDatePickerConfig()
    this.datePickerConfig.showMonths = this.showMonths

    if (this.start) {
      this.setDatesForPicker(this.start)
    }
    if (this.hasOpeningDates && !this.start) {
      this.setCurrentMonthToFirstOpeningDate()
    }
    await nextTick()
    if (!this.$.isUnmounted) {
      this.chargetypeCalendarDataChange()
    }
  }

  beforeUnmount() {
    // Clean up Flatpickr instance to prevent event handlers firing after unmount
    const datePickerRef = this.$refs['date-picker'] as DateRangePicker
    if (datePickerRef?.fp) {
      datePickerRef.fp.destroy()
    }
  }

  get headerRangeDates() {
    if (!this.dates.depart) {
      return {
        arrive: this.dates.arrive,
        depart: this.dates.arrive ? addDays(1, this.dates.arrive) : null,
      }
    }
    return this.dates
  }

  @Watch('dates')
  async onDatesChanged(
    dates: Nullable<ArriveDepartIso>,
    prevDates: Nullable<ArriveDepartIso>,
  ) {
    if (
      dates?.arrive !== prevDates?.arrive ||
      dates?.depart !== prevDates?.depart
    ) {
      await this.$nextTick(() => {
        if (this.$.isUnmounted) return

        const arrive = dates.arrive ? dates.arrive : null
        const depart = dates.depart ? dates.depart : null
        const modelVal = this.datePickerModel

        // These checks prevent datepicker jumping to month selected
        // when user selects second month in desktop calendar or
        // user selects date shown at start of following month on mobile.
        // Same dates get updates as different types ( string v [string, string])
        // which causes reactivity to trigger.
        if (arrive && depart && `${arrive} to ${depart}` === modelVal) {
          return
        }
        if (arrive && arrive === modelVal) {
          return
        }

        this.setPickerState(arrive, depart)
      })
    }
  }

  @Watch('chargetypeCalendarData')
  @Watch('convertedSelectedDatesPrice')
  @Watch('availability', { deep: true })
  chargetypeCalendarDataChange() {
    if (this.$.isUnmounted) return
    ;(this.$refs['date-picker'] as DateRangePicker)?.fp.redraw()
  }

  private setCurrentMonthToFirstOpeningDate() {
    const start = IsoDateFormatter.fromIsoDate(this.closestOpeningDate.start)
    this.currentMonth = start.getMonth()
    this.currentYear = start.getFullYear()
    this.$emit('monthChanged', this.currentMonth)
    this.$emit('yearChanged', this.currentYear)
  }

  private setDatesForPicker(start: Date) {
    this.currentMonth = start.getMonth()
    this.currentYear = start.getFullYear()
    this.setPickerState(start, this.end)
    this.submitDates(start, this.end)
  }

  private get availableDates() {
    return this.dateAvailability?.map((day) => day.date)
  }

  get start(): Date | null {
    if (!this.dates.arrive) {
      return null
    }

    // Flatpickr will use a local date. When we create a date using ISO format
    // with only YYYY-MM-DD it will be UTC with a time of 00:00:00. In timezones
    // with a negative time difference this means date get displayed as the day
    // before arrive date.
    const dateUTC = IsoDateFormatter.fromIsoDate(this.dates.arrive)
    const dateLocal = new Date(
      Date.UTC(
        dateUTC.getUTCFullYear(),
        dateUTC.getUTCMonth(),
        dateUTC.getUTCDate(),
      ),
    )
    return dateLocal
  }

  get end() {
    if (!this.dates.depart) {
      return null
    }

    // See comment above RE timezones.
    const dateUTC = IsoDateFormatter.fromIsoDate(this.dates.depart)
    const dateLocal = new Date(
      Date.UTC(
        dateUTC.getUTCFullYear(),
        dateUTC.getUTCMonth(),
        dateUTC.getUTCDate(),
      ),
    )
    return dateLocal
  }

  convertedSelectedDatesPrice: number | null = null

  @Watch('selectedDatesPrice', { immediate: true, deep: true })
  selectedDatesPriceUpdated() {
    if (this.$.isUnmounted) return

    if (this.selectedDatesPrice) {
      this.convertedSelectedDatesPrice = this.currencyStore
        .convert(
          priceToMoneyType(this.selectedDatesPrice),
          this.currencyStore.currentCurrencyCode,
        )
        .getAmount()
    } else {
      this.convertedSelectedDatesPrice = null
    }
  }

  onDatePickerDatesChange(
    dates: Date[],
    _: string,
    datepicker: FlatPickrInstance,
  ) {
    if (this.$.isUnmounted) return

    this.showingArrivalDays = dates.length !== 1
    const start = dates[0] || null
    let end = dates[1] || null
    const isSameDay =
      start &&
      end &&
      start.getDate() === end.getDate() &&
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear()
    if (isSameDay && start) {
      end = this.getDateModifiedByStep(start)
      datepicker.setDate([start, end])
    }
    if (start && end) {
      this.$emit('changeRange', [start, end])
    } else if (start) {
      this.$emit('changeRange', [start])
    }
    if (!this.dateAvailability && !end) {
      this.addStartRangeClass(datepicker?.selectedDateElem)
    }
    this.submitDates(start, end)

    if (!start && !end) {
      datepicker.changeYear(this.currentYear)
      datepicker.changeMonth(this.currentMonth, false)
    }
  }

  onDatePickerReady(
    _dates: Date[],
    _date: string,
    datepicker: FlatPickrInstance,
  ) {
    if (this.$.isUnmounted) return

    const setCalendarOn = this.getMonthAndYearToOpenCalendarOn()
    if (setCalendarOn) {
      this.currentYear = setCalendarOn.year
      this.currentMonth = setCalendarOn.month
      datepicker.changeYear(setCalendarOn.year)
      datepicker.changeMonth(setCalendarOn.month, false)
    } else {
      this.currentYear = datepicker.currentYear
      this.currentMonth = datepicker.currentMonth
    }
    this.$emit('monthChanged', this.currentMonth)
    this.$emit('yearChanged', this.currentYear)
  }

  getMonthAndYearToOpenCalendarOn() {
    if (this.calendarStartDate && !this.start) {
      const startDate = new Date(this.calendarStartDate)
      return { year: startDate.getFullYear(), month: startDate.getUTCMonth() }
    } else if (this.hasOpeningDates && !this.start) {
      return { year: this.currentYear, month: this.currentMonth }
    }
    return undefined
  }

  onDatePickerMonthChange(
    _dates: Date[],
    _date: string,
    datepicker: FlatPickrInstance,
  ) {
    if (this.$.isUnmounted) return

    this.currentMonth = datepicker.currentMonth
    this.currentYear = datepicker.currentYear
    this.$emit('monthChanged', this.currentMonth)
    this.$emit('yearChanged', this.currentYear)
  }

  onDatePickerYearChange(
    _dates: Date[],
    _date: string,
    datepicker: FlatPickrInstance,
  ) {
    if (this.$.isUnmounted) return

    this.currentYear = datepicker.currentYear
    this.$emit('yearChanged', this.currentYear)
  }

  onDatePickerChange(
    dates: Date[],
    _date: string,
    _datepicker: FlatPickrInstance,
  ) {
    this.$emit('monthChanged', this.currentMonth)
    this.$emit('yearChanged', this.currentYear)
    this.$emit('datesChanged', dates)
  }

  onDatePickerDayCreate(
    dates: Date[],
    _date: string,
    _datepicker: FlatPickrInstance,
    day: FlatPickrDayElement,
  ) {
    if (this.availableDates && this.$route.params.slug) {
      this.availableDatesDayRender(dates, day)
    }
  }

  clearDates() {
    this.$emit('clearDates')
    this.submitDates(null, null)
  }

  private submitDates(start: Date | null = null, end: Date | null = null) {
    const arrive = start ? IsoDateFormatter.toIsoDate(start) : null
    const depart = end ? IsoDateFormatter.toIsoDate(end) : null
    const dateRange = { arrive, depart }
    this.$emit('changeDates', dateRange)
    this.$emit('yearChanged', this.currentYear)
    this.$emit('datesChanged', [start, end])
  }

  private async createDatePickerConfig() {
    const config = await getDatePickerConfig(this.$i18n.locale)
    config.onReady = this.onDatePickerReady
    config.onDayCreate = this.onDatePickerDayCreate
    config.onChange = this.onDatePickerChange
    return config
  }

  private availableDatesDayRender(
    selectedDates: Date[],
    day: FlatPickrDayElement,
  ) {
    const isoDate = IsoDateFormatter.toIsoDate(day.dateObj)
    if (selectedDates.length === 1) {
      const startDayAvailability = this.dateAvailability?.find(
        (d) => d.date === IsoDateFormatter.toIsoDate(selectedDates[0]),
      )
      this.addAvailabilityClass(
        startDayAvailability?.bookable_dates.includes(isoDate) ||
          startDayAvailability?.date === isoDate,
        day,
      )
    } else {
      this.addAvailabilityClass(this.availableDates?.includes(isoDate), day)
    }

    if (
      isoDate === this.dates.arrive &&
      selectedDates.length === 2 &&
      isoDate === IsoDateFormatter.toIsoDate(selectedDates[0])
    ) {
      this.addStayPriceToDay(day)
    } else if (this.chargetypeCalendarData) {
      this.addChargetypeToDay(isoDate, day)
    } else if (this.dateAvailability) {
      // Unified API: Extract pricing from datepickerAvailability
      this.addUnifiedApiPricingToDay(isoDate, day)
    }
  }

  private addAvailabilityClass(available: boolean, day: FlatPickrDayElement) {
    if (this.$.isUnmounted || !day) return
    day.classList.add(available ? 'date-available' : 'date-unavailable')
  }

  private addStartRangeClass(day?: FlatPickrDayElement) {
    if (this.$.isUnmounted || !day) return
    day.classList.add('startRange')
  }

  private digitsAmount = 0

  private addChargetypeToDay(isoDate: string, day: FlatPickrDayElement) {
    const calDayChargetype = this.chargetypeCalendarData[isoDate]
    if (!calDayChargetype || !calDayChargetype.priceFx) {
      return
    }

    const usersPrice =
      calDayChargetype.priceFx[this.currencyStore.currentCurrencyCode] / 100
    let priceObject = puNumber(
      usersPrice,
      this.currencyStore.currentCurrencyCode,
      `minimumFractionDigits:${this.digitsAmount} maximumFractionDigits:${this.digitsAmount}`,
    )

    const nights = calDayChargetype.minDaysViolated
      ? String(calDayChargetype.minDays)
      : String(calDayChargetype.nights)

    this.addPriceToDay(day, priceObject, nights)
  }

  private addUnifiedApiPricingToDay(isoDate: string, day: FlatPickrDayElement) {
    const dayData = this.dateAvailability?.find((d) => d.date === isoDate)
    if (!dayData || !dayData.price || !dayData.price_fx) {
      return
    }
    const usersPrice =
      (dayData as any).price_fx[this.currencyStore.currentCurrencyCode] / 100
    const moneyType = priceToMoneyType({
      amount: Math.round(usersPrice * 100),
      currency: this.currencyStore.currentCurrencyCode,
    })
    const digitsAmount = getMinFractionDigits(moneyType)
    let priceObject = puNumber(
      usersPrice,
      this.currencyStore.currentCurrencyCode,
      `minimumFractionDigits:${digitsAmount} maximumFractionDigits:${digitsAmount}`,
    )

    const nights = (dayData as any).nights
      ? String((dayData as any).nights)
      : '1'

    this.addPriceToDay(day, priceObject, nights)
  }

  private addStayPriceToDay(day: FlatPickrDayElement) {
    if (!this.convertedSelectedDatesPrice) {
      return
    }
    const usersPrice = this.convertedSelectedDatesPrice / 100
    const stayPrice = puNumber(
      usersPrice,
      this.currencyStore.currentCurrencyCode,
      `minimumFractionDigits:${this.digitsAmount} maximumFractionDigits:${this.digitsAmount}`,
    )

    this.addPriceToDay(day, stayPrice, String(this.dateRangeNights))
  }

  private addPriceToDay(
    day: FlatPickrDayElement,
    price: string,
    nights: string,
  ) {
    if (this.$.isUnmounted || !day) return

    const chargetypeElements: (HTMLElement | string)[] = []
    const dom = day.innerHTML
    chargetypeElements.push(dom)
    chargetypeElements.push(this.createDayPricingEl(price, 'quote-price'))
    chargetypeElements.push(this.createDayPricingEl(nights, 'quote-nights'))

    day.innerHTML = ''
    day.append(...chargetypeElements)
  }

  private createDayPricingEl(value: string, elClass: string) {
    const pricingEl = document.createElement('span')
    pricingEl.classList.add(elClass)
    pricingEl.append(value)
    return pricingEl
  }

  get dateRangeNights() {
    const { arrive, depart } = this.dates
    return new DateRange(arrive!, depart!).nights
  }

  private get hasOpeningDates() {
    if (!this.$route.params.slug) {
      return false
    }
    return this.openingDates?.length
  }

  private get closestOpeningDate(): DateRange {
    const start = this.findClosestOpeningDate()
    const end = this.getDateModifiedByStep(start)
    return new DateRange(
      IsoDateFormatter.toIsoDate(start),
      IsoDateFormatter.toIsoDate(end),
    )
  }

  private findClosestOpeningDate(): Date {
    const today = new Date()
    if (this.openingDates) {
      for (const openingDate of this.openingDates) {
        const from = new Date(openingDate.from)
        const to = new Date(openingDate.to)
        if (from <= today && today <= to) {
          return today
        } else if (from > today) {
          return from
        }
      }
    }
    return new Date()
  }

  private getDateModifiedByStep(dateToModify: Date, step: number = 2): Date {
    const date = new Date(dateToModify)
    date.setDate(dateToModify.getDate() + step)
    return date
  }
}

function getDatePickerModel(
  start: string | Date | null,
  end: string | Date | null = null,
) {
  if (!start) {
    return []
  }
  return [start, end]
}

export default toNative(DatesWizardBody)
</script>

<style scoped>
.disabled-button :deep(button) {
  filter: grayscale(1) opacity(0.4);
  pointer-events: none;
}
</style>

<style>
@import url('flatpickr/dist/themes/light.css');

.date-range-picker-input {
  display: none;
}

.flatpickr-calendar.inline {
  border-radius: 0;
  box-shadow: none;
  top: 0;
  font-size: 1em;
  width: 100%;
  margin: auto;
}

.flatpickr-calendar.open,
.flatpickr-calendar.inline {
  max-height: none;
}

.rangeMode .flatpickr-day {
  margin-top: 0;
}

.flatpickr-day {
  color: black;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
}

.flatpickr-day,
.flatpickr-day.prevMonthDay,
.flatpickr-day.nextMonthDay {
  max-width: 100%;
  height: 12vw;
  line-height: 12vw;
  border-radius: 0.4em;
  border: 0.15em solid #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.flatpickr-day.prevMonthDay,
.flatpickr-day.nextMonthDay {
  color: #8d9eb0;
}

.flatpickr-day.prevMonthDay.flatpickr-disabled.date-unavailable,
.flatpickr-day.nextMonthDay.flatpickr-disabled.date-unavailable,
.flatpickr-day.flatpickr-disabled,
.flatpickr-day.flatpickr-disabled:hover {
  color: var(--colour-page-background);
}

.flatpickr-day.flatpickr-disabled.date-unavailable,
.flatpickr-day.flatpickr-disabled.date-unavailable:hover {
  background-color: #fff;
  color: #8d9eb0;
}

.flatpickr-day.prevMonthDay.date-unavailable,
.flatpickr-day.prevMonthDay.date-unavailable:hover,
.flatpickr-day.prevMonthDay.date-unavailable:focus,
.flatpickr-day.nextMonthDay.date-unavailable,
.flatpickr-day.nextMonthDay.date-unavailable:focus,
.flatpickr-day.nextMonthDay.date-unavailable:hover {
  color: #8d9eb0;
}

.flatpickr-day.inRange {
  border-radius: 0;
}

.flatpickr-day.date-available,
.flatpickr-day.inRange.date-available,
.flatpickr-day.nextMonthDay.inRange.date-available,
.flatpickr-day.prevMonthDay.inRange.date-available,
.flatpickr-day.date-available:hover,
.flatpickr-day.date-available:focus {
  background-color: var(--colour-light-green);
  color: #fff;
  font-weight: bold;
  border-color: #fff;
}

.flatpickr-day.prevMonthDay.date-available,
.flatpickr-day.prevMonthDay.date-available:hover,
.flatpickr-day.prevMonthDay.date-available:focus,
.flatpickr-day.nextMonthDay.date-available,
.flatpickr-day.nextMonthDay.date-available:hover,
.flatpickr-day.nextMonthDay.date-available:focus {
  color: #b7e0c1;
}

.flatpickr-day.date-unavailable,
.flatpickr-day.inRange.date-unavailable,
.flatpickr-day.date-unavailable:hover,
.flatpickr-day.date-unavailable:focus {
  background-color: var(--colour-page-background);
  color: var(--colour-dark-blue);
}

.flatpickr-innerContainer,
.dayContainer,
.flatpickr-days {
  width: 100%;
  max-width: 100%;
  min-width: 100%;
  border: none;
  margin-top: 0.15em;
}

.flatpickr-months {
  height: 5em;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.flatpickr-day.today,
.flatpickr-day.today.date-unavailable {
  border-color: white;
}

.flatpickr-day:hover,
.flatpickr-day:focus {
  background: none;
  border: none;
  -webkit-tap-highlight-color: transparent;
}

@media (hover: hover) {
  .flatpickr-day:hover {
    background: #e2e2e2;
    border-color: #e2e2e2;
  }
}

.flatpickr-day.selected,
.flatpickr-day.startRange,
.flatpickr-day.endRange,
.flatpickr-day.selected.inRange,
.flatpickr-day.startRange.inRange,
.flatpickr-day.endRange.inRange,
.flatpickr-day.startRange.prevMonthDay.inRange,
.flatpickr-day.endRange.prevMonthDay.inRange,
.flatpickr-day.startRange.nextMonthDay.inRange,
.flatpickr-day.endRange.nextMonthDay.inRange,
.flatpickr-day.selected:hover,
.flatpickr-day.startRange:hover,
.flatpickr-day.endRange:hover,
.flatpickr-day.selected:focus,
.flatpickr-day.startRange:focus,
.flatpickr-day.endRange:focus,
.flatpickr-day.selected:focus,
.flatpickr-day.startRange:focus,
.flatpickr-day.endRange:focus,
.flatpickr-day.selected.prevMonthDay,
.flatpickr-day.startRange.prevMonthDay,
.flatpickr-day.endRange.prevMonthDay,
.flatpickr-day.selected.nextMonthDay,
.flatpickr-day.startRange.nextMonthDay,
.flatpickr-day.endRange.nextMonthDay {
  border-width: 0.15em;
  background-color: var(--colour-dark-blue);
  border-color: white;
  color: #fff;
  border-top: 0.15em solid white;
  border-bottom: 0.15em solid white;
}

.flatpickr-day.prevMonthDay.date-available.selected,
.flatpickr-day.prevMonthDay.date-available.selected:hover,
.flatpickr-day.prevMonthDay.date-available.selected:focus,
.flatpickr-day.nextMonthDay.date-available.selected,
.flatpickr-day.nextMonthDay.date-available.selected:hover,
.flatpickr-day.nextMonthDay.date-available.selected:focus,
.flatpickr-day.prevMonthDay.date-unavailable.selected,
.flatpickr-day.prevMonthDay.date-unavailable.selected:hover,
.flatpickr-day.prevMonthDay.date-unavailable.selected:focus,
.flatpickr-day.nextMonthDay.date-unavailable.selected,
.flatpickr-day.nextMonthDay.date-unavailable.selected:focus,
.flatpickr-day.nextMonthDay.date-unavailable.selected:hover {
  color: #fff;
}

.flatpickr-day.nextMonthDay.selected.startRange,
.flatpickr-day.prevMonthDay.selected.startRange,
.flatpickr-day.selected.startRange {
  border-left: 0.15em solid white;
}

.flatpickr-day.startRange,
.flatpickr-day.startRange.inRange,
.flatpickr-day.startRange:hover,
.flatpickr-calendar .flatpickr-day.selected {
  border-left: 0.15em solid white;
  border-right: none;
  border-color: white;
}

.flatpickr-day.endRange,
.flatpickr-day.endRange.inRange,
.flatpickr-day.endRange:hover,
.flatpickr-day.selected.endRange {
  border-right: 0.15em solid white;
  border-left: none;
}

.flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n + 1)),
.flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n + 1)),
.flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n + 1)) {
  box-shadow: none;
}

.flatpickr-calendar.multiMonth
  .flatpickr-days
  .dayContainer:nth-child(n + 2)
  .flatpickr-day.inRange:nth-child(7n + 1) {
  box-shadow:
    inset 0px 2px 0 0 var(--colour-dark-blue),
    inset 0px -2px 0 0 var(--colour-dark-blue);
}

.flatpickr-calendar.multiMonth
  .flatpickr-days
  .dayContainer:nth-child(n + 1)
  .flatpickr-day.inRange:nth-child(7n + 7) {
  box-shadow:
    inset 0px 2px 0 0 var(--colour-dark-blue),
    inset 0px -2px 0 0 var(--colour-dark-blue) !important;
}

.flatpickr-day.selected.startRange,
.flatpickr-day.startRange.startRange,
.flatpickr-day.endRange.startRange {
  border-radius: 0.4em 0 0 0.4em;
}

.flatpickr-day.selected.endRange,
.flatpickr-day.startRange.endRange,
.flatpickr-day.endRange.endRange {
  border-radius: 0 0.4em 0.4em 0;
}

/* default selected depart day until overriden by user choice */
.date-picker-without-availability
  .flatpickr-day.selected.startRange
  + .flatpickr-day:not(.endRange):not(.inRange) {
  background: var(--colour-dark-blue);
  border: 0.15em solid #fff;
  border-left: none;
  border-radius: 0 0.4em 0.4em 0;
  color: #fff;
  font-weight: bold;
}

.flatpickr-day.inRange,
.flatpickr-day.inRange:hover,
.flatpickr-day.inRange:focus,
.flatpickr-day.prevMonthDay.inRange,
.flatpickr-day.nextMonthDay.inRange {
  box-shadow:
    inset 0px 2px 0 0 var(--colour-dark-blue),
    inset 0px -2px 0 0 var(--colour-dark-blue);
  background: none;
  border: none;
  border-top: 0.15em solid #fff;
  border-bottom: 0.15em solid #fff;
}

.dates-selected .flatpickr-day.inRange,
.dates-selected .flatpickr-day.inRange:hover,
.dates-selected .flatpickr-day.inRange:focus,
.dates-selected .flatpickr-day.prevMonthDay.inRange,
.dates-selected .flatpickr-day.nextMonthDay.inRange {
  background: var(--colour-dark-blue);
  font-weight: bold;
}

.dates-selected .flatpickr-day.inRange,
.dates-selected .flatpickr-day.prevMonthDay.inRange,
.dates-selected .flatpickr-day.nextMonthDay.inRange {
  color: #fff;
}

.flatpickr-weekdays,
span.flatpickr-weekday,
.flatpickr-months .flatpickr-month {
  background: white;
  color: var(--colour-dark-blue);
}

.flatpickr-weekdays,
.flatpickr-months .flatpickr-month,
.flatpickr-months .flatpickr-prev-month,
.flatpickr-months .flatpickr-next-month {
  height: auto;
}

.flatpickr-months .flatpickr-prev-month:active:before,
.flatpickr-months .flatpickr-next-month:active:before {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  content: '';
  width: 3em;
  height: 3em;
  position: absolute;
  z-index: -1;
}

.flatpickr-months .flatpickr-prev-month,
.flatpickr-months .flatpickr-next-month {
  align-items: center;
  display: flex;
  height: auto;
  justify-content: center;
  padding: 1em;
  margin-top: -1.5em;
  position: relative;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.flatpickr-months .flatpickr-prev-month img,
.flatpickr-months .flatpickr-next-month img {
  width: 2em;
  height: 2em;
}

.flatpickr-months .flatpickr-prev-month img {
  transform: rotate(180deg);
}

.flatpickr-current-month span.cur-month {
  margin: 0;
  font-size: 1.1em;
}

.flatpickr-current-month input.cur-year {
  padding: 0;
  text-align: center;
  pointer-events: none;
  font-size: var(--body);
}

.flatpickr-current-month span.cur-month:hover,
.flatpickr-current-month .numInputWrapper:hover {
  background: white;
}

.flatpickr-current-month {
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: 1.35em;
  height: auto;
  justify-content: center;
  left: auto;
  line-height: initial;
  padding: 0;
  position: relative;
  width: auto;
}

.flatpickr-months {
  margin: 0 -1em;
}

/* year */
.flatpickr-current-month .numInputWrapper {
  box-sizing: content-box;
  flex-shrink: 0;
  margin-top: 0.25em;
  padding: 0 0.75em;
  text-align: center;
  width: 5ch;
  width: 5ch\0;
}

.numInputWrapper span {
  border: none;
  height: 100%;
  opacity: 0.4;
  padding: 0;
  width: 0.75em;
}

.numInputWrapper span:hover {
  background: white;
}

.numInputWrapper span.arrowUp {
  top: 0;
  right: 0;
}

.numInputWrapper span.arrowDown {
  top: 0;
  bottom: 0;
  right: auto;
  left: 0;
}

.numInputWrapper span.arrowUp:after {
  top: 0;
  border: none;
  background: #ffffff
    url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGwtcnVsZT0iZXZlbm9kZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgc3Ryb2tlLW1pdGVybGltaXQ9IjEuNCIgY2xpcC1ydWxlPSJldmVub2RkIiB2aWV3Qm94PSIwIDAgMjYgMTUiPgogIDxwYXRoIGZpbGw9IiM2NjYiIGQ9Ik0zLjEuNWw5LjYgOS42TDIyLjMuNUExLjggMS44IDAgMSAxIDI1IDMuMWwtMTEgMTFjLS4zLjMtLjguNS0xLjMuNS0uNCAwLTEtLjItMS4zLS42TC41IDMuMUExLjggMS44IDAgMCAxIDMuMS41eiIvPgo8L3N2Zz4K)
    no-repeat center right;
  background-size: contain;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.numInputWrapper span.arrowDown:after {
  top: 0;
  border: none;
  background: #ffffff
    url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGwtcnVsZT0iZXZlbm9kZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgc3Ryb2tlLW1pdGVybGltaXQ9IjEuNCIgY2xpcC1ydWxlPSJldmVub2RkIiB2aWV3Qm94PSIwIDAgMjYgMTUiPgogIDxwYXRoIGZpbGw9IiM2NjYiIGQ9Ik0zLjEuNWw5LjYgOS42TDIyLjMuNUExLjggMS44IDAgMSAxIDI1IDMuMWwtMTEgMTFjLS4zLjMtLjguNS0xLjMuNS0uNCAwLTEtLjItMS4zLS42TC41IDMuMUExLjggMS44IDAgMCAxIDMuMS41eiIvPgo8L3N2Zz4K)
    no-repeat center left;
  background-size: contain;
  width: 100%;
  height: 100%;
  transform: rotate(90deg);
}

.flatpickr-months .flatpickr-prev-month.flatpickr-disabled {
  display: inline;
  opacity: 0.1;
  pointer-events: none;
}

.date-picker__selection-key {
  margin: 1em 0 1.5em;
}

.date-picker__selection-key .flatpickr-day {
  height: 2em;
  flex-basis: 2em;
  margin: 0 0.5em auto 0;
  flex-shrink: 0;
}

.date-picker__selection-key,
.date-picker__selection-key-text {
  display: flex;
}

.date-picker__selection-key-text {
  flex-direction: column;
  justify-content: center;
}

.date-picker__selection-key-img {
  height: 1.2em;
  margin: 0 0.15em;
}

.date-picker__selection-key-desc {
  font-size: var(--body);
}

/* desktop & tablet */
.desktop .dayContainer {
  min-width: 21em;
}

.date-picker-months-2 .flatpickr-month + .flatpickr-month {
  margin-left: 12em;
}

.date-picker-months-2
  .flatpickr-weekdaycontainer
  + .flatpickr-weekdaycontainer {
  margin-left: 5em;
}

.date-picker-months-2 .dayContainer + .dayContainer {
  margin-left: 5em;
  box-shadow: none;
}

.desktop .dayContainer .flatpickr-day,
.desktop .dayContainer .flatpickr-day.prevMonthDay,
.desktop .dayContainer .flatpickr-day.nextMonthDay {
  height: auto;
  line-height: normal;
  min-height: 3.56em !important;
  min-width: 3em;
}

.desktop .date-picker__selection-key .flatpickr-day {
  height: 2em;
  width: 2em;
  min-height: 2em;
  min-width: 2em;
}

.desktop .date-picker__selection-key {
  margin: 1em 0 0.5em;
}

@media (max-height: 780px) {
  .desktop .flatpickr-day,
  .desktop .flatpickr-day.prevMonthDay,
  .desktop .flatpickr-day.nextMonthDay {
    min-height: 2em;
    min-width: 2em;
  }
}

@container (max-width: 340px) {
  .desktop.date-picker .flatpickr-day,
  .desktop.date-picker .flatpickr-day.prevMonthDay,
  .desktop.date-picker .flatpickr-day.nextMonthDay {
    min-height: 2em;
    min-width: 2em;
  }
}
</style>
